import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { html } from 'lit';
import type { Company } from '../db/company.js';
import { escapeHtmlAttribute } from '../shared/escape-html.js';
import {
  applicationSubmittedMailTemplate,
  buildUserGreeting,
  candidateBookingMailTemplate,
  candidateFeedbackMailTemplate,
  candidateReminderMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from './mail-templates.js';

//* Booking Mail
export const CandidateBookingMail = defineMailTemplate(MailTemplateType.INTERVIEW_BOOKED_CANDIDATE, {
  displayName: 'Termin gebucht (Kandidat:in)',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: candidateBookingMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.TIME_SLOT,
    MailTemplateVars.MEETING_LINK,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in ein Interview gebucht hat
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    meetingLink: string,
    jobTitle: string,
    timeSlot: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle,
        [MailTemplateVars.TIME_SLOT]: timeSlot,
        [MailTemplateVars.MEETING_LINK]: escapeHtmlAttribute(meetingLink),
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

//* Reminder Mails
export const CandidateReminderMail = defineMailTemplate(MailTemplateType.CANDIDATE_REMINDER, {
  displayName: 'Erinnerung Vorstellungsgespräch (Kandidat:in)',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: candidateReminderMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.TIME_SLOT,
    MailTemplateVars.MEETING_LINK,
    MailTemplateVars.DAYS_UNTIL_INTERVIEW,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in ein Interview gebucht hat
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    meetingLink: string,
    jobTitle: string,
    timeSlot: string,
    daysUntilInterview: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle,
        [MailTemplateVars.TIME_SLOT]: timeSlot,
        [MailTemplateVars.MEETING_LINK]: escapeHtmlAttribute(meetingLink),
        [MailTemplateVars.DAYS_UNTIL_INTERVIEW]: daysUntilInterview,
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

export const CandidateFeedbackMail = defineMailTemplate(MailTemplateType.CANDIDATE_FEEDBACK, {
  displayName: 'Feedback nach Gespräch (Kandidat:in)',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: candidateFeedbackMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in ein Interview gebucht hat
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitle: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle ?? '---',
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

export const CandidateApplicationSubmittedMail = defineMailTemplate(MailTemplateType.APPLICATION_SUBMITTED, {
  displayName: 'Bewerbung eingereicht',
  defaultTemplate: applicationSubmittedMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLES,
    MailTemplateVars.COMPANY_NAMES,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn eine Bewerbung eingereicht wird
    </p>
    <p>
      Hinweis:<br />
      - Bei mehreren Unternehmen werden die Namen mit ", " verbunden
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitles: string,
    companyNames: string,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLES]: jobTitles,
        [MailTemplateVars.COMPANY_NAMES]: companyNames,
      },
      scope: 'default',
    };
  },
});
